import React, { useEffect, useMemo, useState } from 'react';
import { TextField, Grid, Typography, Button, Checkbox } from '@mui/material';
import ActionConfirmation from '../elements/ActionConfirmation';
import CountrySelect from './CountrySelect';
import type { ActionConfirmationVariant } from '../../types';
import { useUser } from '../../contexts/UserContext';
import StripeCardInput from './StripeCardInput';
import { BillingFormProps } from '../../types';
import { getPaymentMethodAcceptedCreditCardBrands } from '../../hooks/cart/AcceptedPaymentMethodBrands';
import { getLocationId } from '../../utils';
import { usePublicSettings } from '../../hooks/usePublicSettings';
import { capitalizeFirstLetter } from '../../utils';
import { useScrollToConfirmation } from '../../hooks/useScrollToConfirmation';
import { getPostalFieldConfig, normalizePostalCode, validatePostalCode } from '../../utils/postalCode';

const BillingForm: React.FC<BillingFormProps> = ({ billingInfo, setBillingInfo, handleNext }) => {
  const { user } = useUser();
  const [confirmation, setConfirmation] = useState<{ visible: boolean; variant: ActionConfirmationVariant; message: string }>({ visible: false, variant: 'info', message: '' });
  const [stripeData, setStripeData] = useState({ token: '', brand: '', tokenPreferred: '' });
  const [acceptedPaymentMethodCreditCardBrands, setAcceptedPaymentMethodCreditCardBrands] = useState<string[]>([]);
  const [zipCodeError, setZipCodeError] = useState('');
  const { companyName } = usePublicSettings();
  const locationUUID = getLocationId();
  const postalFieldConfig = useMemo(
    () => getPostalFieldConfig(billingInfo.country),
    [billingInfo.country],
  );
  const zipInputProps = useMemo(
    () => ({
      maxLength: postalFieldConfig.maxLength,
      inputMode: postalFieldConfig.inputMode,
    }),
    [postalFieldConfig.maxLength, postalFieldConfig.inputMode],
  );

  useEffect(() => {
    if (!locationUUID) return;

    (async () => {
      const acceptedMethodsResponse = await getPaymentMethodAcceptedCreditCardBrands(locationUUID);
      console.log('acceptedMethodsResponse', acceptedMethodsResponse);
      if (acceptedMethodsResponse.statusCode === 200)
        setAcceptedPaymentMethodCreditCardBrands(acceptedMethodsResponse.data);
    })();
  }, [locationUUID]);

  const handleBillingInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = event.target;

    if (name === 'zipCode') {
      setBillingInfo((prev) => {
        const normalized = normalizePostalCode(prev.country, value);
        return normalized === prev.zipCode ? prev : { ...prev, zipCode: normalized };
      });
      if (zipCodeError) setZipCodeError('');
      return;
    }

    setBillingInfo((prev) => ({
      ...prev,
      [name]: name === 'savePreferred' ? checked : value,
    }));
  };

  const handleZipCodeBlur = () => {
    if (!billingInfo.zipCode) return;
    const result = validatePostalCode(billingInfo.country, billingInfo.zipCode);
    setZipCodeError(result.valid ? '' : result.message);
  };

  const handleCountryChange = (newCountryCode: string) => {
    // Clear the postal code on country switch so a value entered for the previous
    // country's format doesn't trigger a validation error against the new rules.
    setBillingInfo((prev) => ({ ...prev, country: newCountryCode, zipCode: '' }));
    setZipCodeError('');
  };

  const handleNextClick = () => {
    if (
      !billingInfo.firstName ||
      !billingInfo.lastName ||
      !billingInfo.email ||
      !billingInfo.address ||
      !billingInfo.phone ||
      !billingInfo.city ||
      !billingInfo.zipCode ||
      !billingInfo.country
    ) {
      return setConfirmation({ visible: true, variant: 'error', message: 'Please fill all required fields.' });
    }

    const postalValidation = validatePostalCode(billingInfo.country, billingInfo.zipCode);
    if (!postalValidation.valid) {
      setZipCodeError(postalValidation.message);
      return setConfirmation({ visible: true, variant: 'error', message: postalValidation.message });
    }

    if (!stripeData.token || !stripeData.brand) {
      return setConfirmation({ visible: true, variant: 'error', message: 'Invalid credit card details.' });
    }

    if (!acceptedPaymentMethodCreditCardBrands.includes(stripeData.brand)) {
      return setConfirmation({
        visible: true,
        variant: 'error',
        message: `${companyName} does not accept ${capitalizeFirstLetter(stripeData.brand)} at this location.`,
      });
    }

    handleNext(stripeData);
  };

  useEffect(() => {
    if (user?.email) {
      setBillingInfo((prev) => ({ ...prev, email: user.email }));
    }
  }, [user, setBillingInfo]);

  useScrollToConfirmation(confirmation.visible, confirmation.message, 'billing-form-confirmation');

  const handleDismissConfirmation = () => {
    setConfirmation((c) => ({ ...c, visible: false }));
  };

  return (
    <div>
      <ActionConfirmation
        id="billing-form-confirmation"
        visible={confirmation.visible}
        variant={confirmation.variant}
        message={confirmation.message}
        onDismiss={handleDismissConfirmation}
      />
      <Typography variant="h6">Billing Information</Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            label="First Name"
            required
            fullWidth
            name="firstName"
            value={billingInfo.firstName}
            onChange={handleBillingInputChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Last Name"
            required
            fullWidth
            name="lastName"
            value={billingInfo.lastName}
            onChange={handleBillingInputChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Email"
            required
            fullWidth
            name="email"
            value={billingInfo.email}
            onChange={handleBillingInputChange}
            disabled
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Phone"
            required
            fullWidth
            name="phone"
            value={billingInfo.phone}
            onChange={handleBillingInputChange}
          />
        </Grid>
        <Grid item xs={12}>
          <CountrySelect value={billingInfo.country} onChange={handleCountryChange} />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="State/Province"
            required
            fullWidth
            name="state"
            value={billingInfo.state}
            onChange={handleBillingInputChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="City"
            required
            fullWidth
            name="city"
            value={billingInfo.city}
            onChange={handleBillingInputChange}
          />
        </Grid>
        <Grid item xs={12} lg={8}>
          <TextField
            label="Address"
            required
            fullWidth
            name="address"
            value={billingInfo.address}
            onChange={handleBillingInputChange}
          />
        </Grid>
        <Grid item xs={6} lg={4}>
          <TextField
            label={postalFieldConfig.label}
            required
            fullWidth
            name="zipCode"
            value={billingInfo.zipCode}
            onChange={handleBillingInputChange}
            onBlur={handleZipCodeBlur}
            error={!!zipCodeError}
            helperText={zipCodeError || ''}
            placeholder={postalFieldConfig.placeholder}
            inputProps={zipInputProps}
          />
        </Grid>
        <Grid item xs={12}>
          {billingInfo.firstName &&
            billingInfo.lastName &&
            billingInfo.email &&
            billingInfo.address &&
            billingInfo.phone &&
            billingInfo.city &&
            billingInfo.zipCode &&
            billingInfo.country && <StripeCardInput setStripeData={setStripeData} billingInfo={billingInfo} />}
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h6">
            <Checkbox
              checked={billingInfo.savePreferred}
              onChange={(e) => handleBillingInputChange(e)}
              name="savePreferred"
              sx={{
                paddingLeft: '0px',
                paddingTop: '4px',
              }}
            />
            Save as preferred payment method.
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" color="primary" onClick={handleNextClick} className="next-button">
            Next
          </Button>
        </Grid>
      </Grid>

    </div>
  );
};

export default BillingForm;
